import { useMutation } from '@tanstack/react-query';
import { exportCashFlowToSheets, type CashFlowRequestOptions, type SheetsExportResponse } from '@/lib/cashFlow';

/**
 * React mutation hook that fires `POST /api/cash-flow/export-sheets?year=YYYY`.
 *
 * Usage:
 *   const { mutate: exportSheets, isPending, data, error } = useSheetsExport(year, options);
 *   <button disabled={isPending} onClick={() => exportSheets()}>Ekspordi Sheets</button>
 *
 * On success, `data.url` is the Google Sheets URL — show it as an "Ava tabel" link.
 * The mutation is not added to the query cache (no invalidation needed — the sheet
 * is a one-shot write-only artifact).
 */
export function useSheetsExport(
  year: number,
  options?: CashFlowRequestOptions,
): {
  mutate: () => void;
  isPending: boolean;
  data: SheetsExportResponse | undefined;
  error: Error | null;
  reset: () => void;
} {
  const { mutate, isPending, data, error, reset } = useMutation<SheetsExportResponse, Error, void>({
    mutationFn: () => exportCashFlowToSheets(year, options),
  });
  return { mutate, isPending, data, error, reset };
}
